import Title from "../../../Shared/Title";

import StatusPopups from "../../../Shared/Form/StatusPopups";
import { useEffect, useState } from "react";
import Button from "../../../Shared/Form/Button";
import BusyArea from '../../../Shared/BusyArea';
import { useAppSelector } from "../../../../Redux/store";
import { Hydra } from "../../../../Utils/Hydra";
import { faXmark, faPlus } from "@fortawesome/pro-solid-svg-icons";
import moment from "moment";
import { useLazyGetSponsorDataQuery } from "../../../../Redux/Api/Organisations";
import LoadingPage from "../../LoadingPage";
import { useNavigate } from "react-router-dom";
import { useDeleteSponsorMutation } from "../../../../Redux/Api/Sponsors";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion, AnimatePresence } from "framer-motion";
import GenericDatagrid from "../../../Datagrid/GenericDatagrid";
import ImageRenderer from "../../../Datagrid/Renderers/ImageRenderer";
import { faEdit, faImage, faInfinity, faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import RowActions, { RowAction } from "../../../Datagrid/DatagridLayout/Rows/RowActions";
import StatusRenderer, { getStatusDetails } from "../../../Datagrid/Renderers/StatusRenderer";
import { GridRenderCellParams, GridValueGetterParams, GridRowParams } from '@mui/x-data-grid';
import { SponsorResponseItem } from '../../../../Redux/Api/Request/Sponsors';

const SponsorListPage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const org_id = useAppSelector((s) => s.organisation.id)!;

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [successAddMessage, setSuccessAddMessage] = useState('');
    const [selectedIcon, setSelectedIcon] = useState<string | null>(null);

    const [getSponsorData, { data }] = useLazyGetSponsorDataQuery();
    const [deleteSponsor] = useDeleteSponsorMutation();

    const isMediumScreen = useMediaQuery('(min-width: 1325px)');
    const isSmallScreen = useMediaQuery('(min-width: 725px)');

    useEffect(() => {
        if (sessionStorage.getItem('successMessage')) {
            setSuccessAddMessage(sessionStorage.getItem('successMessage')!);
            sessionStorage.removeItem('successMessage');
        }
    }, [org_id]);

    useEffect(() => {
        if (org_id)
            getSponsorData({
                id: org_id
            });
    }, [org_id, getSponsorData]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && selectedIcon || event.key === "Tab" && selectedIcon) {
                closeIconPreview();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [selectedIcon]);

    const deleteSponsorAction = async (id: string) => {
        if (busy) return;

        setBusy(true);
        setError('');
        setSuccessMessage('');

        try {
            await deleteSponsor(id);
            if (org_id)
                getSponsorData({
                    id: org_id
                });
            setSuccessMessage(t('sponsor:sponsorDeleted') as string);
        } catch (e) {
            let err = e as Hydra.Error;
            setError(err['hydra:description']);
        }
        setBusy(false);
    };

    const showIcon = (icon: string) => {
        setSelectedIcon(icon);
    }

    const closeIconPreview = () => {
        setSelectedIcon(null);
    }

    const checkIfActive = (from: string, until: string) => {
        const now = new Date();
        const from_date = new Date(from);
        const until_date = new Date(until);

        return now >= from_date && now <= until_date;
    }

    const columns = [
        {
            field: 'icon',
            headerName: '',
            renderCell: (params: GridRenderCellParams<SponsorResponseItem>) => (
                <ImageRenderer src={params.row.icon} alt={params.row.title} onClick={() => showIcon(params.row.icon)} />
            ),
            maxWidth: 60,
            minWidth: 60,
            filterable: false,
        },
        {
            field: 'hiddenName',
            headerName: t('sponsor:hiddenName'),
            valueGetter: (params: GridValueGetterParams<SponsorResponseItem>) => (
                params.row.hiddenName?.trim()
            ),
            flex: 1,
        },
        {
            field: 'name',
            headerName: t('sponsor:name'),
            valueGetter: (params: GridValueGetterParams<SponsorResponseItem>) => (
                params.row.name?.trim()
            ),
            flex: 1,
            hide: !isSmallScreen,
        },
        {
            field: 'status',
            headerName: t('sponsor:status'),
            flex: 1,
            minWidth: isMediumScreen ? 120 : 50,
            maxWidth: isMediumScreen ? 120 : 50,
            valueGetter: (params: GridValueGetterParams<SponsorResponseItem>) => {
                const { text } = getStatusDetails(checkIfActive(params.row.from, params.row.until) ? 'active' : 'inactive');
                return text;
            },
            renderCell: (params: GridRenderCellParams<SponsorResponseItem>) => (
                <StatusRenderer status={checkIfActive(params.row.from, params.row.until) ? 'active' : 'inactive'} small={!isMediumScreen} />
            ),
        },
        {
            field: 'from',
            headerName: t('sponsor:activeFrom'),
            flex: 1,
            valueGetter: (params: GridValueGetterParams<SponsorResponseItem>) => {
                return moment(params.row.from).format('DD-MM-YYYY');
            },
            hide: !isSmallScreen,
            type: 'date',
        },
        {
            field: 'until',
            headerName: t('sponsor:activeUntil'),
            flex: 1,
            valueGetter: (params: GridValueGetterParams<SponsorResponseItem>) => {
                return moment(params.row.until).format('DD-MM-YYYY');
            },
            renderCell: (params: GridRenderCellParams<SponsorResponseItem>) => {
                const formatted = moment(params.row.until).format('DD-MM-YYYY');
                const renderMap: Record<string, JSX.Element> = {
                    'Invalid date': <span>Invalid date</span>,
                    '01-01-9999': <FontAwesomeIcon icon={faInfinity} />,
                };
                return renderMap[formatted] || <span>{formatted}</span>;
            },
            hide: !isSmallScreen,
            type: 'date',
        },
        {
            field: 'actions',
            headerName: '',
            width: 120,
            renderCell: (params: GridRenderCellParams<SponsorResponseItem>) => {
                const actions: RowAction[] = [
                    {
                        label: t('revealImage'),
                        icon: <FontAwesomeIcon icon={faImage} />,
                        onClick: () => { showIcon(params.row.icon); }
                    },
                    {
                        label: t('cards:list:adjust'),
                        icon: <FontAwesomeIcon icon={faEdit} />,
                        onClick: () => navigate(`/${i18n.language}/dashboard/sponsor/edit/` + params.row.id),
                    },
                    {
                        label: t('cards:list:delete'),
                        icon: <FontAwesomeIcon icon={faTrashAlt} />,
                        withModal: {
                            onYes: () => { deleteSponsorAction(params.row.id); }
                        }
                    }
                ];

                return <RowActions actions={actions} />;
            },
        }
    ];

    if (!data) return <LoadingPage />;

    return (
        <>

            <div className='flex flex-wrap justify-between items-center mb-3'>
                <Title text={t('sponsor:title')} />
                <Button
                    action={() => navigate(`/${i18n.language}/dashboard/sponsor/create`)}
                    className="my-6"
                    title={t('sponsor:add')}
                    icon={faPlus}
                    iconright
                />
            </div>
            <StatusPopups setText={setError} type='error' text={error} />
            <StatusPopups setText={setSuccessMessage} type='success' text={successMessage} />
            <StatusPopups setText={setSuccessAddMessage} type='success' text={successAddMessage} />

            <BusyArea busy={busy}>
                <GenericDatagrid
                    data={data}
                    columns={columns}
                    onRowClick={(params: GridRowParams<SponsorResponseItem>) => navigate(`/${i18n.language}/dashboard/sponsor/edit/` + params.row.id)}
                    className='mb-5 lg:mb-10'
                    disableHeaderCells={['icon']}
                />
            </BusyArea>

            <AnimatePresence>
                {selectedIcon && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeIconPreview} className="fixed top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center z-[1000]">
                        <FontAwesomeIcon icon={faXmark} className="absolute top-4 right-4 text-4xl text-white cursor-pointer" />
                        <motion.img initial={{ scale: 0.5 }} animate={{ scale: 1 }} exit={{ scale: 1.2 }} src={selectedIcon} alt="Sponsor Logo Preview" className="max-h-[calc(100vh-100px)] max-w-[calc(100vw-100px)]" />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default SponsorListPage;
